import './fabricDefaults.ts';
import {
    Canvas,
    Group,
    Rect,
    Shadow,
    Textbox,
    classRegistry
} from 'fabric';

export const StickyNoteColors = [
    '#FFF9B1', // yellow (default)
    '#F5D128', // dark yellow
    '#D5F692', // light green
    '#C9DF56', // green
    '#F5A683', // orange
    '#FFCEE0', // pink
    '#A6CCF5', // blue
    '#BE9BF1' // purple
];

export const StickyNoteTextSizes = {
    xsmall: 11,
    small: 14,
    medium: 18,
    large: 26
};

const DEFAULT_SIZE = 200;
const MIN_SIZE = 60;
const TEXT_PADDING = 16;
const DEFAULT_FONT_SIZE = StickyNoteTextSizes.small;
const TEXT_COLOR = '#1F1F1F';

export type StickyNoteTextAlign = 'left' | 'center' | 'right';

export interface StickyNoteOptions {
    left?: number;
    top?: number;
    width?: number;
    height?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
    flipX?: boolean;
    flipY?: boolean;
    fill?: string;
    text?: string;
    fontSize?: number;
    textAlign?: StickyNoteTextAlign;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: 'normal' | 'italic';
}

export const StickyNoteDefaultFontFamily = 'Poppins, sans-serif';

/*
 * A Miro-style sticky note: one indivisible canvas object (drag / resize /
 * rotate / persist as a single element) built as a v7 group of a rounded,
 * coloured body and a centred Textbox. Double-click enters native in-place
 * text editing (see enterTextEditing). Resizing is a real dimension change,
 * not a zoom: after each resize gesture the scale is baked into width/height
 * (see bakeScale), so the font size stays constant and the text area gains
 * or loses wrapping room. The body auto-grows when the text needs more lines.
 */
export class StickyNote extends Group {
    static type = 'sticky-note';

    body: Rect;
    textbox: Textbox;

    /*
     * The note's nominal size. The rendered body grows beyond this when the
     * text needs more room — more lines than the height allows, or a word
     * too long to wrap at this width — and shrinks back as text is removed.
     * Only the base size is serialized; the growth is derived from the text
     * on restore.
     */
    private baseWidth: number;
    private baseHeight: number;

    constructor(options: StickyNoteOptions = {}) {
        const {
            text = '',
            fill = StickyNoteColors[0],
            fontSize = DEFAULT_FONT_SIZE,
            textAlign = 'center',
            fontFamily = StickyNoteDefaultFontFamily,
            fontWeight = 'normal',
            fontStyle = 'normal',
            width = DEFAULT_SIZE,
            height = DEFAULT_SIZE,
            ...groupOptions
        } = options;

        const body = new Rect({
            left: -width / 2,
            top: -height / 2,
            width,
            height,
            rx: 4,
            ry: 4,
            fill,
            // Never a click target of its own: clicks land on the note even
            // while the group is interactive during text editing.
            selectable: false,
            evented: false,
            shadow: new Shadow({
                color: 'rgba(0, 0, 0, 0.25)',
                blur: 8,
                offsetX: 0,
                offsetY: 4
            })
        });

        const textbox = new Textbox(text, {
            left: -width / 2 + TEXT_PADDING,
            top: 0,
            width: width - 2 * TEXT_PADDING,
            fontSize,
            fontFamily,
            fontWeight,
            fontStyle,
            fill: TEXT_COLOR,
            textAlign,
            /*
             * The textbox must receive events (in-place editing) but must
             * never be transformable on its own: it remains the active
             * object right after editing, and without these locks its
             * rotate/scale handles would transform the text out of sync
             * with the note body.
             */
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true
        });

        super([body, textbox], {
            subTargetCheck: true,
            interactive: false,
            lockScalingFlip: true
        });

        this.body = body;
        this.textbox = textbox;
        this.baseWidth = width;
        this.baseHeight = height;

        this.on('modified', this.bakeScale);

        this.textbox.on('changed', () => {
            this.layoutContent();
        });
        this.layoutContent();

        // Geometry is applied after construction: the group's own layout has
        // computed its size from the children by now, so left/top/scale/angle
        // from options position the finished note.
        this.set(groupOptions);
        this.setCoords();
    }

    /*
     * Keeps the text vertically centred and grows/shrinks the body so the
     * text always fits: rendered height is the base height or the text
     * height plus padding, whichever is larger. The group's own position is
     * pinned across the re-layout — without that, fit-content layout drifts
     * the note a little on every pass; with it, the note grows downwards
     * from a fixed top-left and returns exactly when lines are removed.
     */
    private layoutContent() {
        const {left, top} = this;

        /*
         * The textbox never wraps narrower than its longest word: fabric
         * clamps textbox.width up to that minimum but never shrinks it back,
         * so re-assert the nominal width first and let it re-clamp for the
         * current text. The body then follows the text in both directions:
         * nominal size or what the text needs, whichever is larger.
         */
        this.textbox.set(
            'width',
            Math.max(this.baseWidth - 2 * TEXT_PADDING, 1)
        );
        const width = Math.max(
            this.baseWidth,
            this.textbox.width + 2 * TEXT_PADDING
        );
        const height = Math.max(
            this.baseHeight,
            this.textbox.height + 2 * TEXT_PADDING
        );

        this.body.set({
            left: -width / 2,
            top: -height / 2,
            width,
            height
        });
        this.textbox.set({
            left: -this.textbox.width / 2,
            top: -this.textbox.height / 2
        });

        this.triggerLayout();
        this.set({left, top});
        this.setCoords();
        this.dirty = true;
    }

    setColor(color: string) {
        this.body.set('fill', color);
        this.dirty = true;
    }

    get color(): string {
        return this.body.fill as string;
    }

    setFontSize(fontSize: number) {
        this.textbox.set('fontSize', fontSize);
        this.layoutContent();
    }

    get fontSize(): number {
        return this.textbox.fontSize;
    }

    setTextAlign(textAlign: StickyNoteTextAlign) {
        this.textbox.set('textAlign', textAlign);
        this.layoutContent();
    }

    get textAlign(): StickyNoteTextAlign {
        return this.textbox.textAlign as StickyNoteTextAlign;
    }

    setFontFamily(fontFamily: string) {
        this.textbox.set('fontFamily', fontFamily);
        this.layoutContent();
    }

    get fontFamily(): string {
        return this.textbox.fontFamily;
    }

    setBold(bold: boolean) {
        this.textbox.set('fontWeight', bold ? 'bold' : 'normal');
        this.layoutContent();
    }

    get bold(): boolean {
        return this.textbox.fontWeight === 'bold';
    }

    setItalic(italic: boolean) {
        this.textbox.set('fontStyle', italic ? 'italic' : 'normal');
        this.layoutContent();
    }

    get italic(): boolean {
        return this.textbox.fontStyle === 'italic';
    }

    /*
     * Native in-place editing of the nested textbox. The group is made
     * interactive only for the duration of the edit so that mouse events
     * (cursor placement, text selection) reach the child; the rest of the
     * time the note is targeted, moved and selected as one unit.
     */
    enterTextEditing(canvas: Canvas) {
        this.interactive = true;
        const textbox = this.textbox;

        canvas.setActiveObject(textbox);
        textbox.enterEditing();
        textbox.setSelectionStart(textbox.text.length);
        textbox.setSelectionEnd(textbox.text.length);

        textbox.once('editing:exited', () => {
            this.interactive = false;
            this.layoutContent();

            /*
             * If the textbox is still the selection (edit ended via Escape
             * or a click on the note itself), hand the selection back to the
             * note so subsequent rotate/resize gestures transform the whole
             * note. When the user clicked another object or empty canvas,
             * leave their selection alone.
             */
            if (canvas.getActiveObject() === textbox) {
                canvas.setActiveObject(this);
            }

            canvas.requestRenderAll();
        });

        canvas.requestRenderAll();
    }

    /*
     * Applies persisted element properties (initial restore diffs and remote
     * multiplayer merges). Child-owned properties (text, fill, fontSize,
     * width/height) are routed to the children; everything else is group
     * geometry and applied directly.
     */
    applyElementProps(properties: Record<string, unknown>) {
        const {
            text,
            fill,
            fontSize,
            textAlign,
            fontFamily,
            fontWeight,
            fontStyle,
            width,
            height,
            ...groupProperties
        } = properties;

        if (typeof text === 'string' && text !== this.textbox.text) {
            this.textbox.set('text', text);
        }
        if (typeof fontSize === 'number') {
            this.textbox.set('fontSize', fontSize);
        }
        if (typeof textAlign === 'string') {
            this.textbox.set('textAlign', textAlign);
        }
        if (typeof fontFamily === 'string') {
            this.textbox.set('fontFamily', fontFamily);
        }
        if (typeof fontWeight === 'string') {
            this.textbox.set('fontWeight', fontWeight);
        }
        if (typeof fontStyle === 'string') {
            this.textbox.set('fontStyle', fontStyle);
        }
        if (typeof fill === 'string') {
            this.setColor(fill);
        }
        if (typeof width === 'number' || typeof height === 'number') {
            this.resizeTo(
                typeof width === 'number' ? width : this.body.width,
                typeof height === 'number' ? height : this.body.height
            );
        }

        this.set(groupProperties);
        this.layoutContent();
        this.dirty = true;
        this.setCoords();
    }

    private resizeTo(width: number, height: number) {
        this.baseWidth = width;
        this.baseHeight = height;
        this.layoutContent();
    }

    /*
     * A resize gesture changes scaleX/scaleY; convert that into real
     * width/height once the gesture ends so the text keeps its font size and
     * rewraps into the new area instead of being zoomed. Re-fires
     * object:modified so the baked dimensions are what gets persisted.
     */
    private bakeScale = () => {
        if (this.scaleX === 1 && this.scaleY === 1) {
            return;
        }

        const width = Math.max(MIN_SIZE, this.body.width * this.scaleX);
        const height = Math.max(MIN_SIZE, this.body.height * this.scaleY);

        this.set({scaleX: 1, scaleY: 1});
        this.resizeTo(width, height);

        if (this.canvas) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.canvas.fire('object:modified' as any, {target: this});
            this.canvas.requestRenderAll();
        }
    };

    /*
     * Serialized as a compact custom element instead of a generic group with
     * children: smaller data, trivially versionable, and the restore path
     * simply reconstructs the note from these options.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toObject(): any {
        return {
            type: 'sticky-note',
            left: this.left,
            top: this.top,
            width: this.baseWidth,
            height: this.baseHeight,
            scaleX: this.scaleX,
            scaleY: this.scaleY,
            angle: this.angle,
            flipX: this.flipX,
            flipY: this.flipY,
            fill: this.color,
            text: this.textbox.text,
            fontSize: this.textbox.fontSize,
            textAlign: this.textbox.textAlign,
            fontFamily: this.textbox.fontFamily,
            fontWeight: this.textbox.fontWeight,
            fontStyle: this.textbox.fontStyle
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static fromObject(object: any): Promise<StickyNote> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {type, version, ...options} = object ?? {};
        return Promise.resolve(new StickyNote(options as StickyNoteOptions));
    }
}

classRegistry.setClass(StickyNote);
